import React from "react";
import { dateLabel } from "./api";
export type ProposalTermsData = {
  scope: string;
  priceCents: number;
  currency: "USD";
  turnaroundHours: number;
  additionalTerms: string;
  demo?: boolean;
  feeMode?: "test" | "live";
  clarificationRounds?: number;
  clarificationDays?: number;
};
export const proposalPrice = (terms: ProposalTermsData) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: terms.currency,
  }).format(terms.priceCents / 100);
export default function ProposalTerms({
  terms,
  dueAt,
  compact = false,
  request,
  charged = false,
  attorney = false,
}: {
  terms: ProposalTermsData;
  dueAt?: string;
  compact?: boolean;
  request?: string;
  /** Whether this proposal is paid by card rather than a no-charge demo. */
  charged?: boolean;
  /** Word the follow-up terms for the attorney rather than the client. */
  attorney?: boolean;
}) {
  const repeatedIntro = request
    ? `A written attorney answer to: ${request}\n\n`
    : "";
  const scope =
    compact && repeatedIntro && terms.scope.startsWith(repeatedIntro)
      ? terms.scope.slice(repeatedIntro.length)
      : terms.scope;
  return (
    <div
      className={`proposal-terms${compact ? " proposal-terms-compact" : ""}`}
    >
      <div className="proposal-facts">
        <div>
          <span>{"Fixed fee"}</span>
          <strong>{proposalPrice(terms)}</strong>
          {!compact && (
            <small>
              {!charged
                ? "Beta test — no charge will be made"
                : terms.priceCents > 0
                  ? "Paid by card once both sides agree"
                  : "No charge"}
            </small>
          )}
        </div>
        <div>
          <span>Turnaround</span>
          <strong>{terms.turnaroundHours} hours</strong>
          <small>
            {dueAt
              ? `Due ${dateLabel(dueAt)}`
              : charged && terms.priceCents > 0
                ? "Clock starts when the card is charged · calendar hours"
                : "Clock starts when both sides agree · calendar hours"}
          </small>
        </div>
      </div>
      {terms.clarificationRounds !== undefined && (
        <p className="proposal-clarification">
          <strong>Included follow-up:</strong>{" "}
          {terms.clarificationRounds === 0
            ? "No follow-up questions included."
            : attorney
              ? `${terms.clarificationRounds} clarification ${
                  terms.clarificationRounds === 1 ? "round" : "rounds"
                }${terms.clarificationDays != null ? ` within ${terms.clarificationDays} days of delivery` : ""}.`
              : `${
                  terms.clarificationRounds === 1
                    ? "one follow-up question"
                    : `${terms.clarificationRounds} follow-up questions`
                }${terms.clarificationDays != null ? ` within ${terms.clarificationDays} days of the answer` : ""}.`}
          <small>
            {" "}
            {attorney
              ? `${terms.clarificationRounds > 0 ? "A round is the client's follow-up messages until you reply. " : ""}Document requests must fit the scope shown below; new questions need a new review.`
              : `${terms.clarificationRounds > 0 ? "A follow-up includes your messages until the attorney replies. " : ""}Requested documents must fit the scope shown below; a new question needs a new review.`}
          </small>
        </p>
      )}
      <h3>Scope and written deliverable</h3>
      <div className="review-prose proposal-scope">
        {scope.split(/\n\n/).map((paragraph, i) => (
          <p
            key={i}
            className={
              paragraph.startsWith("Not included:")
                ? "proposal-exclusions"
                : undefined
            }
          >
            {paragraph}
          </p>
        ))}
      </div>
      {terms.additionalTerms && (
        <details
          open={compact ? undefined : true}
          className="proposal-additional"
        >
          <summary>Additional terms</summary>
          <p className="review-prose">{terms.additionalTerms}</p>
        </details>
      )}
    </div>
  );
}
